import { TargettedSystemContext, Window } from 'nots-window-renderer/lib';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Subject } from 'rxjs';

import { ComposedActions } from '../../helpers/actions';
import { CommunicationMessageTypes } from '../../helpers/constants';
import createLogger, { LoggerColumns } from '../../helpers/logger';
import { CaptureBTPoint } from '../../helpers/types';
import useCommunicator from '../../hooks/useCommunicator';
import actionService from '../../store/actions.service';
import {
  uiBTCapture_AC,
  uiUserSetWindowAttribute_AC,
} from '../../store/actions/executor.actions';
import {
  IFocusMessage,
  IKeyboardMessage,
} from '../../types/communicator-types';
import DialogContainer from './DialogContainer';
import useMapImage from './hooks/useMapImage';

const logger = createLogger('bundle:NWR', {
  tags: [],
  columns: [LoggerColumns.NOTS_WINDOW_RENDERER],
});

export interface IWindowDisplayManagerProps {
  monitor?: string;
  area: string;
  maxHeight?: number;
  targetSystem: any;
  showBackDrop?: boolean;
  generalData?: any;
  zIndex?: number;
}

const windowEvents$ = new Subject<any>();

function DialogContainerComponent(props: any) {
  const {
    dWindow,
    showBackDrop,
    targetSystem,
    windowData,
    resources,
    correctedDisplayWindows,
    onFieldUpdate,
    onButtonClick,
    onToggleButtonClick,
    onRowSelect,
    onCellSelect,
    onTabClick,
    dispatch,
    onBTCapture,
    onFocusChange,
    onTableCheckboxClick,
    url,
    corners,
    windowEvents$,
    actionService,
    logger,
  } = props;
  useEffect(() => {
    console.log('Initializing DialogContainerComponent');
  }, []);

  return (
    <DialogContainer
      key={dWindow.window}
      showBackDrop={showBackDrop}
      id={dWindow.window}
    >
      <TargettedSystemContext.Provider value={targetSystem}>
        <Window
          key={dWindow.window}
          defaultValues={windowData[dWindow.window]}
          values={windowData[dWindow.window] || {}}
          window={{
            ...resources[dWindow.window],
            ...correctedDisplayWindows[dWindow.window],
          }}
          onFieldUpdate={(key, value) =>
            onFieldUpdate(dWindow.window, key, value)
          }
          onButtonClick={(key: string) => onButtonClick(dWindow.window, key)}
          onToggleButtonClick={(key: string, value: boolean) => {
            onToggleButtonClick(dWindow.window, key, value);
          }}
          onRowSelect={(identifier, rowIndex, isSelected) =>
            onRowSelect(dWindow.window, identifier, rowIndex, isSelected)
          }
          onCellSelect={(identifier, rowIndex, colId, isSelected) =>
            onCellSelect(
              dWindow.window,
              identifier,
              rowIndex,
              colId,
              isSelected
            )
          }
          onTabChange={(key: string) => onTabClick(dWindow.window, key)}
          onDispatchAction={(action: any) => dispatch(action)}
          onBTCapture={(buttonId: string, point: CaptureBTPoint) =>
            onBTCapture(dWindow.window, buttonId, point)
          }
          onFocusChange={onFocusChange}
          onTableCellCheckboxClick={(
            key: string,
            rowIndex: number,
            colId: string,
            value: boolean
          ) =>
            onTableCheckboxClick(dWindow.window, key, rowIndex, colId, value)
          }
          mapDetails={{
            mapImage: url,
            leftUpperCorner: corners.leftUpperCorner,
            rightLowerCorner: corners.rightLowerCorner,
            scale: 0,
            selectedPoints: [],
          }}
          overWriteReadOnly
          eventStream$={windowEvents$}
          actionStream$={actionService}
          logger={logger as any}
        />
      </TargettedSystemContext.Provider>
    </DialogContainer>
  );
}

const MemoizedDialogContainer = React.memo(DialogContainerComponent);

/**
 *
 * @param maxHeight set it to make sure the window is bottom aligned if the window is taller than the maxHeight
 * @returns
 */

export default function WindowDisplayManager(
  props: IWindowDisplayManagerProps
) {
  const dispatch = useDispatch();
  const {
    maxHeight,
    monitor = 'upper',
    showBackDrop = false,
    generalData = {},
    zIndex = 333,
  } = props;
  const executor = useSelector((state: any) => state.executor);
  const resources = useSelector((state: any) => state.resources);
  const [displayWindows, setDisplayWindows] = useState<any>({});
  const [windowData, setWindowData] = useState<any>({});
  const { url, corners } = useMapImage(monitor);
  const onFieldUpdate = useCallback(
    (windowId: string, key: string, value: any) => {
      dispatch(uiUserSetWindowAttribute_AC(windowId, key, value));
    },
    [dispatch]
  );
  const keyboardCommunicator = useCommunicator<IKeyboardMessage>(
    CommunicationMessageTypes.KeyboardEvent
  );
  const focusEventCommunicator = useCommunicator<IFocusMessage>(
    CommunicationMessageTypes.FocusControlEvent
  );

  useEffect(() => {
    console.log('Initializing WindowDisplayManager');
  }, []);

  const onToggleButtonClick = useCallback(
    (windowId: string, key: string, value: boolean) => {
      if (key.toLowerCase().startsWith('text_togglebutton')) {
        dispatch(
          ComposedActions.PushWindowTextToggleButton.aCreator({
            windowId,
            key,
            value,
          })
        );
      } else {
        dispatch(
          ComposedActions.PushWindowToggleButton.aCreator({
            windowId,
            key,
            value,
          })
        );
      }
    },
    [dispatch]
  );

  const onButtonClick = useCallback(
    (windowId: string, key: string) => {
      dispatch(
        ComposedActions.PushWindowButton.aCreator({
          windowId,
          buttonId: key,
        })
      );
      // dispatch(uiWindowBtnClickAC(windowId, key));
    },
    [dispatch]
  );

  const onTabClick = useCallback(
    (windowId: string, key: string) => {
      dispatch(
        ComposedActions.SetWindowActiveTab.aCreator({
          windowId,
          tabId: key,
        })
      );
      // dispatch(uiTabClicked_AC(windowId, key));
    },
    [dispatch]
  );

  const onBTCapture = useCallback(
    (windowId: string, buttonId: string, point: CaptureBTPoint) => {
      console.log('BTCapture', buttonId, point);
      dispatch(
        uiBTCapture_AC({
          buttonId,
          captureAttributes: point,
          windowId,
        })
      );
    },
    [dispatch]
  );

  const onFocusChange = useCallback(
    (windowId: string, widgetId: string) => {
      focusEventCommunicator?.postMessage({
        windowId,
        widgetId,
      });
      windowEvents$.next({
        isControlEvent: true,
        isKeyboardEvent: false,
        payload: {
          windowId,
          widgetId,
        },
      });
    },
    [focusEventCommunicator]
  );

  useEffect(() => {
    keyboardCommunicator?.receiveMessage((payload) => {
      windowEvents$.next({
        isControlEvent: false,
        isKeyboardEvent: true,
        payload,
      });
    });
  }, [keyboardCommunicator]);

  useEffect(() => {
    focusEventCommunicator?.receiveMessage((payload) => {
      const { windowId, widgetId } = payload;
      windowEvents$.next({
        isControlEvent: true,
        isKeyboardEvent: false,
        payload: {
          windowId,
          widgetId,
        },
      });
    });
  }, [focusEventCommunicator]);

  const onRowSelect = useCallback(
    (
      windowId: string,
      identifier: string,
      rowIndex: string,
      isSelected: boolean
    ) => {
      const selectPayload = {
        window: windowId,
        [identifier]: [
          {
            rowNumber: rowIndex,
            selected: isSelected,
          },
        ],
      };
      if (isSelected) {
        dispatch(
          ComposedActions.SelectWindowDataItems.aCreator({
            actionPayload: selectPayload,
          })
        );
      } else {
        dispatch(
          ComposedActions.UnselectWindowDataItems.aCreator({
            actionPayload: selectPayload,
          })
        );
      }
    },
    [dispatch]
  );

  const onCellSelect = useCallback(
    (
      windowId: string,
      identifier: string,
      rowIndex: number,
      colId: string,
      isSelected: boolean
    ) => {
      const selectPayload = {
        window: windowId,
        [identifier]: [
          {
            rowNumber: rowIndex,
            [`${colId}_selected`]: isSelected,
          },
        ],
      };
      if (isSelected) {
        dispatch(
          ComposedActions.SelectWindowDataItemCell.aCreator({
            actionPayload: selectPayload,
          })
        );
      } else {
        dispatch(
          ComposedActions.UnselectWindowDataItemCell.aCreator({
            actionPayload: selectPayload,
          })
        );
      }
    },
    [dispatch]
  );
  const onTableCheckboxClick = useCallback(
    (
      windowId: string,
      key: string,
      rowIndex: number,
      colId: string,
      value: boolean
    ) => {
      const payload = {
        window: windowId,
        table: {
          tableId: key,
          colId,
          rowNumber: rowIndex,
          value,
        },
      };
      dispatch(
        ComposedActions.UserClickWindowDataItemCellCheckbox.aCreator({
          actionPayload: payload,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    console.log('WindowDisplayManager -> executor', executor);
    setDisplayWindows(executor.displayWindows);
    setWindowData(executor.windows);
  }, [executor]);
  const [correctedDisplayWindows, setCorrectedDisplayWindows] =
    useState(displayWindows);
  useEffect(() => {
    if (!maxHeight) {
      setCorrectedDisplayWindows(displayWindows);
      return;
    }
    const newObj: any = {};
    Object.keys(displayWindows).map((windowName) => {
      newObj[windowName] = {
        ...displayWindows[windowName],
      };
      if (
        maxHeight &&
        maxHeight < +resources[windowName].attributes.geometry.height
      ) {
        newObj[windowName].yPosition =
          maxHeight - resources[windowName].attributes.geometry.height - 3;
      }
      setCorrectedDisplayWindows(newObj);
    });
  }, [displayWindows, resources]);

  const filteredWindows = useMemo(() => {
    console.log('Recalculating filtered windows');
    return Object.keys(displayWindows)
      .map((dWindow) => displayWindows[dWindow])
      .filter((dWindow) => dWindow.monitor === monitor)
      .filter((dWindow) => dWindow.displayArea === props.area)
      .filter((dWindow) => dWindow.visible);
  }, [displayWindows, monitor, props.area]);

  return (
    <div
      style={{
        position: 'absolute',
        zIndex,
      }}
    >
      {filteredWindows.map((dWindow) => {
        return (
          <MemoizedDialogContainer
            key={dWindow.window}
            dWindow={dWindow}
            showBackDrop={showBackDrop}
            targetSystem={props.targetSystem}
            windowData={windowData}
            resources={resources}
            correctedDisplayWindows={correctedDisplayWindows}
            onFieldUpdate={onFieldUpdate}
            onButtonClick={onButtonClick}
            onToggleButtonClick={onToggleButtonClick}
            onRowSelect={onRowSelect}
            onCellSelect={onCellSelect}
            onTabClick={onTabClick}
            dispatch={dispatch}
            onBTCapture={onBTCapture}
            onFocusChange={onFocusChange}
            onTableCheckboxClick={onTableCheckboxClick}
            url={url}
            corners={corners}
            windowEvents$={windowEvents$}
            actionService={actionService}
            logger={logger}
          />
        );
      })}
    </div>
  );
}
